import { ReactFlowProvider } from '@xyflow/react';
import type { ProjectFile } from '@bits2schema/shared';
import { CanvasView } from '../canvas/CanvasView';
import { CanvasStoreProvider } from '../canvas/store';
import { Inspector } from './Inspector';
import { NavigationTree } from './NavigationTree';
import { ResizablePanel } from './ResizablePanel';
import { WorkspaceSelectionProvider } from './selection';

interface WorkspaceProps {
  project: ProjectFile;
  /**
   * Epic 14 (E14-US10) — vizuální/interakční blokace, dokud projekt edituje
   * jiný uživatel. Skutečnou pojistkou proti zápisu je 409 na serveru
   * (`assertLeaseForWrite`, epic-14 kap. 3) — tohle je jen UX vrstva nad tím,
   * ne náhrada.
   */
  readOnly?: boolean;
  /** Epic 15, E15-US4 — portal cíl pro `CanvasToolsMenu` v `.toolbar__actions` (`ProjectPage.tsx`). */
  toolsMenuSlot: HTMLDivElement | null;
}

/**
 * Třípanelové rozvržení (Epic 11): navigační strom | canvas (Epic 6) | inspektor.
 * `CanvasStoreProvider` je tu, ne v `CanvasView`, protože strom i inspektor
 * potřebují přístup ke stejnému canvas stavu a výběru jako canvas samotný
 * (epic-11 kap. 8: "jedno sdílené selection state"). `SchemaStoreProvider`
 * (Epic 4) žije o úroveň výš, v `ProjectPage`, protože ho čte i souhrn a
 * přehled tabulek mimo tento třípanel — strom/canvas/inspektor si `schema`
 * berou přímo z `useSchemaStore()`, stejně jako `canvas` z `useCanvasStore()`.
 */
export function Workspace({ project, readOnly, toolsMenuSlot }: WorkspaceProps) {
  return (
    <ReactFlowProvider>
      <CanvasStoreProvider projectId={project.id} initialCanvas={project.canvas}>
        <WorkspaceSelectionProvider>
          <div className={`workspace${readOnly ? ' workspace--readOnly' : ''}`} aria-disabled={readOnly}>
            <ResizablePanel side="left" storageKey="bits2schema.panel.tree" defaultWidth={260} minWidth={200} maxWidth={420}>
              <NavigationTree />
            </ResizablePanel>
            <div className="workspace__canvas">
              <CanvasView
                projectId={project.id}
                projectName={project.name}
                projectOrigin={project.origin}
                toolsMenuSlot={toolsMenuSlot}
              />
            </div>
            <ResizablePanel side="right" storageKey="bits2schema.panel.inspector" defaultWidth={320} minWidth={240} maxWidth={520}>
              <Inspector />
            </ResizablePanel>
          </div>
        </WorkspaceSelectionProvider>
      </CanvasStoreProvider>
    </ReactFlowProvider>
  );
}
